import type { EventRecord } from "@/types/records";
import { useParams, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getEventBySlugWithProducer } from "@/lib/api";
import { getEventPublicPath, getEventPublicBuyPath } from "@/lib/urls";

/**
 * Event booking route — redirects to the event detail page with the tickets tab active.
 * All purchases now go through the unified cart → checkout flow which collects
 * attendee data (name, email, CPF) for every ticket.
 *
 * Legacy support: if accessed via the old route (/eventos/:slug/comprar) without
 * a producer username, the event is fetched to resolve the canonical URL with
 * the producer slug, ensuring QR codes and printed materials keep working.
 */
export default function EventBooking() {
  const { username, slug } = useParams<{ username?: string; slug?: string }>();

  // When accessed from legacy route (/eventos/:slug/comprar), fetch the event
  // to discover the producer's organizer_slug and redirect to the canonical URL.
  const { data: event, isLoading } = useQuery({
    queryKey: ["event-legacy-redirect", slug],
    queryFn: () => getEventBySlugWithProducer(slug!),
    enabled: !!slug && !username,
    staleTime: Infinity,
    gcTime: 5 * 60 * 1000,
  });

  const producer = (event as EventRecord)?.profiles || null;
  const organization = (event as EventRecord)?.organizations || null;
  const isOrg = !!event?.organization_id && !!organization?.slug;
  const producerSlug = isOrg ? organization.slug : producer?.organizer_slug;

  // Legacy route without producer username — resolve canonical URL
  if (!username) {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      );
    }
    if (producerSlug && slug) {
      return <Navigate to={getEventPublicBuyPath(slug, producerSlug, isOrg)} replace />;
    }
    // Event not found or missing producer — send to events listing
    return <Navigate to="/eventos" replace />;
  }

  // Canonical route with producer username — redirect to detail page (tickets tab)
  const isOrgRoute = window.location.pathname.startsWith("/organizacao/");
  return <Navigate to={`${getEventPublicPath(slug || "", username, isOrgRoute)}?tab=tickets`} replace />;
}
